import { Media, MediaType } from '#types/generated/graphql-types';
import type { Translate } from 'next-translate';
import Link from 'next/link';
import getRoute from '#utils/getRoute';
export const SUPPORTED_LANGUAGE_CODES = ['de', 'en', 'fr'] as const;

export const translateLanguageCodes = (
  languageCodes: string[] | null | undefined,
  t: Translate
): string | null => {
  if (!languageCodes?.length) return null;
  const codes = languageCodes.flatMap((code) =>
    code === 'any' ? [...SUPPORTED_LANGUAGE_CODES] : [code]
  );
  const translated = [...new Set(codes)].map((code) => t(code));
  return translated.join(', ') || null;
};

export const formatAttributeDate = (dateString?: string | null) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

// Reusable styled link, used for both series and studio
export const AttributeLink = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => {
  return (
    <Link href={href} legacyBehavior>
      <a className="tw-underline tw-text-[var(--color-link)] hover:tw-text-[var(--color-link-hover)] tw-transition-colors">
        {children}
      </a>
    </Link>
  );
};

// "attributeType" value: series title (+ season/episode) for episodes
export const getSeriesEpisodeAttributeValue = (
  media: Media,
  t: Translate,
  isEpisode: boolean
) => {
  if (!isEpisode || media.mediaType !== MediaType.Episode || !media.series) {
    return undefined;
  }

  const seriesUrl = getRoute(media.series, t);

  const seasonStr =
    // "season_one" here refers to the singular form of the word "Season"
    typeof media.season?.seasonNumber === 'number'
      ? `${t('season_one')} ${media.season.seasonNumber}`
      : null;
  const episodeStr =
    typeof media.episodeNumber === 'number'
      ? `${t('episode')} ${media.episodeNumber}`
      : null;

  const valueParts = [seasonStr, episodeStr].filter(Boolean);
  const suffix = valueParts.length > 0 ? `, ${valueParts.join(', ')}` : null;

  return (
    <>
      {seriesUrl ? (
        <AttributeLink href={seriesUrl}>{media.series.title}</AttributeLink>
      ) : (
        media.series.title
      )}
      {suffix}
    </>
  );
};

// "gridTabStudios" value: studio title, linked if a route exists
export const getStudioAttributeValue = (media: Media, t: Translate) => {
  if (!media.studio) return undefined;

  const studioUrl = getRoute(media.studio, t);
  return studioUrl ? (
    <AttributeLink href={studioUrl}>{media.studio.title}</AttributeLink>
  ) : (
    media.studio.title
  );
};
